import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Checkbox, Input, InputNumber, Select, Switch, Table, notification } from "antd";
import { ClockCircleOutlined, LinkOutlined, LockOutlined, ReloadOutlined, ThunderboltOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { cancelPhoneTriggerCall, getPhoneTrigger, queuePhoneTriggerTest } from "./surveyService";
import { DEFAULT_CALLING_HOURS, DEFAULT_PHONE_TRIGGER } from "./constants";
import { Notice, QueueStatus, TRIGGER_KEY as K, resultText, statusTag } from "./phoneTriggerShared";
import styles from "./style.module.scss";

const DAYS = [0, 1, 2, 3, 4, 5, 6];
// Settings → Integrations → CRM connection: the company key, webhook address and sample request.
const CRM_SETTINGS_PATH = "/settings/integrations?view=crm";

/**
 * When a voice survey may call (calling hours, for lists and events alike) and the CRM trigger:
 * the company's CRM posts an event (e.g. a checkout) naming this survey and the customer is
 * called after the delay. The key and webhook live in Settings → Integrations → CRM connection;
 * here are the survey's own settings, a test call and its queue. Settings take effect when the
 * survey is saved. While phone calls are locked the trigger is shown switched off and cannot be
 * turned on.
 */
const PhoneTriggerPanel = ({ instrumentId, phoneCall, channelSaved, locked = false, onChange }) => {
  const { t } = useTranslation();
  const hours = { ...DEFAULT_CALLING_HOURS, ...(phoneCall.callingHours || {}) };
  const trigger = { ...DEFAULT_PHONE_TRIGGER, ...(phoneCall.trigger || {}), ...(locked ? { enabled: false } : {}) };
  const [info, setInfo] = useState(null);
  const [testPhone, setTestPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const saved = !!(instrumentId && channelSaved);

  const setHours = (patch) => onChange({ callingHours: { ...hours, ...patch } });
  const setTrigger = (patch) => onChange({ trigger: { ...trigger, ...patch } });

  const load = useCallback(async () => {
    const res = await getPhoneTrigger(instrumentId);
    if (res && !res.error) setInfo(res);
  }, [instrumentId]);

  useEffect(() => {
    if (saved && trigger.enabled) load();
  }, [saved, trigger.enabled, load]);

  const errorMessage = (res) => t(`${K}.error_${res?.reason}`, { defaultValue: t(`${K}.error`) });

  const sendTest = async () => {
    setBusy(true);
    const res = await queuePhoneTriggerTest(instrumentId, { phone: testPhone });
    setBusy(false);
    if (res && !res.error) {
      notification.success({ message: t(`${K}.test_queued`) });
      load();
    } else {
      notification.error({ message: errorMessage(res) });
    }
  };

  const cancelRow = async (rowId) => {
    const res = await cancelPhoneTriggerCall(instrumentId, rowId);
    if (res && !res.error) load();
    else notification.error({ message: errorMessage(res) });
  };

  const columns = [
    { title: t(`${K}.col_phone`), dataIndex: "phone", key: "phone" },
    { title: t(`${K}.col_name`), dataIndex: "name", key: "name" },
    {
      title: t(`${K}.col_status`),
      key: "status",
      render: (_, row) => <QueueStatus t={t} row={row} />,
    },
    { title: t(`${K}.col_result`), key: "result", render: (_, row) => resultText(t, row) },
    {
      title: t(`${K}.col_due`),
      key: "due",
      render: (_, row) => (row.due_at ? dayjs(row.due_at).format("D MMM HH:mm") : ""),
    },
    { title: t(`${K}.col_attempts`), dataIndex: "attempts", key: "attempts" },
    {
      key: "actions",
      render: (_, row) =>
        row.status === "waiting" ? (
          <Button size="small" type="link" onClick={() => cancelRow(row.id)}>
            {t(`${K}.cancel`)}
          </Button>
        ) : null,
    },
  ];

  return (
    <div className={styles.phonePanel}>
      <div className={styles.phoneFormat}>
        <div className={styles.phoneFormatTitle}>
          <ClockCircleOutlined /> {t(`${K}.hours_title`)}
        </div>
        <p className={styles.fieldHint}>{t(`${K}.hours_hint`)}</p>
        <div className={styles.distGrid2}>
          <div className={styles.distField}>
            <label className={styles.fieldLabel}>{t(`${K}.hours_start`)}</label>
            <Input type="time" value={hours.start} onChange={(e) => setHours({ start: e.target.value || "10:00" })} />
          </div>
          <div className={styles.distField}>
            <label className={styles.fieldLabel}>{t(`${K}.hours_end`)}</label>
            <Input type="time" value={hours.end} onChange={(e) => setHours({ end: e.target.value || "20:00" })} />
          </div>
        </div>
        <div className={styles.distField}>
          <label className={styles.fieldLabel}>{t(`${K}.days_off`)}</label>
          <Checkbox.Group
            value={hours.daysOff}
            onChange={(days) => setHours({ daysOff: days })}
            options={DAYS.map((d) => ({ value: d, label: t(`${K}.day_${d}`) }))}
          />
        </div>
        <div className={styles.phoneVoiceRow}>
          <Switch checked={hours.skipHolidays} onChange={(v) => setHours({ skipHolidays: v })} />
          <span>{t(`${K}.skip_holidays`)}</span>
        </div>
      </div>

      <div className={styles.phoneFormat}>
        <div className={`${styles.phoneTestHeader} ${locked ? styles.phoneLocked : ""}`}>
          <div>
            <div className={styles.phoneFormatTitle}>
              <ThunderboltOutlined /> {t(`${K}.title`)}
            </div>
            <p className={styles.fieldHint}>{t(`${K}.desc`)}</p>
          </div>
          <Switch checked={trigger.enabled} disabled={locked} onChange={(v) => setTrigger({ enabled: v })} />
        </div>
        {locked ? (
          <p className={styles.fieldHint}>
            <LockOutlined /> {t("surveyManagement.distribute.phone.calls_locked")}
          </p>
        ) : null}

        {trigger.enabled ? (
          <>
            <div className={styles.distGrid2}>
              <div className={styles.distField}>
                <label className={styles.fieldLabel}>{t(`${K}.delay`)}</label>
                <InputNumber
                  min={0}
                  max={720}
                  value={trigger.delayHours}
                  addonAfter={t(`${K}.hours_unit`)}
                  onChange={(v) => setTrigger({ delayHours: v ?? 0 })}
                />
                <p className={styles.fieldHint}>{t(`${K}.delay_hint`)}</p>
              </div>
              <div className={styles.distField}>
                <label className={styles.fieldLabel}>{t(`${K}.frequency`)}</label>
                <InputNumber
                  min={0}
                  max={365}
                  value={trigger.frequencyDays}
                  addonAfter={t(`${K}.days_unit`)}
                  onChange={(v) => setTrigger({ frequencyDays: v ?? 0 })}
                />
                <p className={styles.fieldHint}>{t(`${K}.frequency_hint`)}</p>
              </div>
              <div className={styles.distField}>
                <label className={styles.fieldLabel}>{t(`${K}.retry`)}</label>
                <Select
                  value={trigger.maxAttempts}
                  onChange={(v) => setTrigger({ maxAttempts: v })}
                  options={[1, 2, 3].map((n) => ({ value: n, label: t(`${K}.attempts_${n}`) }))}
                />
              </div>
              {trigger.maxAttempts > 1 ? (
                <div className={styles.distField}>
                  <label className={styles.fieldLabel}>{t(`${K}.retry_after`)}</label>
                  <InputNumber
                    min={1}
                    max={72}
                    value={trigger.retryAfterHours}
                    addonAfter={t(`${K}.hours_unit`)}
                    onChange={(v) => setTrigger({ retryAfterHours: v ?? 3 })}
                  />
                </div>
              ) : null}
            </div>

            {!saved ? <p className={styles.fieldHint}>{t(`${K}.save_first`)}</p> : null}
            {saved && info ? (
              <>
                {info.calling && !info.calling.ready ? (
                  <Notice>{t(`${K}.dry_run_notice`)}</Notice>
                ) : null}

                <div className={styles.distField}>
                  <label className={styles.fieldLabel}>{t(`${K}.crm_title`)}</label>
                  <p className={styles.fieldHint}>{t(`${K}.crm_hint`, { id: instrumentId })}</p>
                  <div>
                    <Button
                      icon={<LinkOutlined />}
                      href={CRM_SETTINGS_PATH}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t(`${K}.crm_open`)}
                    </Button>
                  </div>
                </div>

                <div className={styles.distField}>
                  <label className={styles.fieldLabel}>{t(`${K}.test`)}</label>
                  <div className={styles.phoneVoiceRow}>
                    <Input
                      value={testPhone}
                      placeholder="0532 123 45 67"
                      onChange={(e) => setTestPhone(e.target.value)}
                    />
                    <Button loading={busy} disabled={!testPhone.trim()} onClick={sendTest}>
                      {t(`${K}.test_send`)}
                    </Button>
                  </div>
                  <p className={styles.fieldHint}>{t(`${K}.test_hint`)}</p>
                </div>

                <div className={styles.phoneTestHeader}>
                  <div className={styles.phoneVoiceRow}>
                    {Object.entries(info.counts || {}).map(([status, count]) => (
                      <span key={status}>
                        {statusTag(t, status)}
                        {count}
                      </span>
                    ))}
                  </div>
                  <Button size="small" icon={<ReloadOutlined />} onClick={load} />
                </div>
                <Table
                  size="small"
                  rowKey="id"
                  columns={columns}
                  dataSource={info.rows || []}
                  pagination={false}
                  locale={{ emptyText: t(`${K}.queue_empty`) }}
                />
              </>
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  );
};

export default PhoneTriggerPanel;
